import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_LOYALTY_PROGRAM } from '../../../apollo/loyaltyProgram/Query';

// Customer loyalty program data and functions
export const useLoyaltyProgram = () => {
  // Customer points state
  const [customers, setCustomers] = useState([]);

  const [searchCustomer, setSearchCustomer] = useState('');

  const [getLoyaltyPrograms, { data: loyaltyData, loading: loyaltyLoading, error: loyaltyError }] = useLazyQuery(GET_LOYALTY_PROGRAM, { fetchPolicy: 'cache-and-network' });

  // Remove auto-fetch on mount; fetch will be triggered explicitly by caller (e.g., on tab select)
  // useEffect(() => {
  //   getLoyaltyPrograms();
  // }, []);

  const formatMoney = (value) => (value != null ? new Intl.NumberFormat().format(value) : '0');

  useEffect(() => {
    if (loyaltyData?.data) {
      const mapped = loyaltyData.data.map((item, idx) => ({
        id: item?.user?.id || idx + 1,
        name: item?.orderGroup?.customerName || `${item?.user?.firstName || ''} ${item?.user?.lastName || ''}`.trim() || 'Unknown',
        phone: '-',
        points: item?.point || 0,
        totalPoints: item?.point || 0,
        usedPoints: 0,
        availablePoints: item?.point || 0,
        totalSpent: formatMoney(item?.money),
        status: item?.status,
        shopName: item?.shop?.name,
        updatedAt: item?.updatedAt,
        createdAt: item?.createdAt,
      }));
      setCustomers(mapped);
    }
  }, [loyaltyData]);

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchCustomer.toLowerCase()) ||
    customer.phone.includes(searchCustomer)
  );

  // Customer management functions
  const addCustomer = (newCustomer) => {
    const customer = {
      ...newCustomer,
      id: customers.length + 1,
      totalPoints: newCustomer.points || 0,
      usedPoints: 0,
      availablePoints: newCustomer.points || 0,
      totalSpent: '0'
    };
    setCustomers([...customers, customer]);
  };

  const updateCustomer = (customerId, updatedData) => {
    setCustomers(customers.map(customer => 
      customer.id === customerId 
        ? { ...customer, ...updatedData }
        : customer
    ));
  };

  const deleteCustomer = (customerId) => {
    setCustomers(customers.filter(customer => customer.id !== customerId));
  };

  const addPointsToCustomer = (customerId, points) => {
    setCustomers(customers.map(customer => 
      customer.id === customerId 
        ? { 
            ...customer, 
            points: customer.points + points,
            totalPoints: customer.totalPoints + points,
            availablePoints: customer.availablePoints + points
          }
        : customer
    ));
  };

  const useCustomerPoints = (customerId, points) => {
    setCustomers(customers.map(customer => 
      customer.id === customerId 
        ? { 
            ...customer, 
            points: customer.points - points,
            usedPoints: customer.usedPoints + points,
            availablePoints: customer.availablePoints - points
          }
        : customer
    ));
  };

  return {
    customers,
    setCustomers,
    searchCustomer,
    setSearchCustomer,
    filteredCustomers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addPointsToCustomer,
    useCustomerPoints,
    loyaltyData,
    loyaltyLoading,
    loyaltyError,
    refresh: getLoyaltyPrograms
  };
};

export default useLoyaltyProgram;